import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  endpoints,
  OUTREACH_STAGE_META,
  type Channel,
  type OutreachStage,
  type UpdateChannelContactRequest,
} from "@/lib/api";
import { fmtTimestamp } from "@/lib/format";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { cn } from "@/lib/utils";
import { Field } from "@/components/ui/Field";

// ChannelCRMSection — v1.4 — operator-facing contact info + outreach status.
// Bridges the 2-4 week negotiation window between lifecycle=candidate and
// lifecycle=active. BD manages outreach_stage independently of lifecycle;
// last_contacted_at auto-bumps on every edit.
//
// v2.4.8 — extracted from ChannelDetail.tsx (renamed from CRMSection so the
// import name reads naturally outside its host route).

// Single source of truth lives in api.ts (OUTREACH_STAGE_META). Aliased here so
// the badge map can't drift from the OutreachStage union or InlineEditCell.
const OUTREACH_STAGES = OUTREACH_STAGE_META;

export function ChannelCRMSection({ channel }: { channel: Channel }) {
  const [editing, setEditing] = useState(false);
  const stage = OUTREACH_STAGES[(channel.OutreachStage || "idle") as OutreachStage] ?? OUTREACH_STAGES.idle;
  const hasAnyContact =
    channel.ContactEmail ||
    channel.ContactTelegram ||
    channel.ContactDiscord ||
    channel.ContactAgent;

  // Days since last contact, for the "last contacted N days ago" hint.
  let lastContactRelative: string | null = null;
  if (channel.LastContactedAt) {
    const ms = Date.now() - new Date(channel.LastContactedAt).getTime();
    const days = Math.floor(ms / 86_400_000);
    lastContactRelative = days === 0 ? "今天" : `${days} 天前`;
  }

  // Highlight overdue follow-ups.
  let followupOverdue = false;
  if (channel.NextFollowupAt) {
    followupOverdue = new Date(channel.NextFollowupAt).getTime() < Date.now();
  }

  return (
    <>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold">
          BD 联络 (CRM)
          <Badge variant={stage.variant} className="ml-2">
            {stage.label}
          </Badge>
        </h2>
        <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
          编辑
        </Button>
      </div>

      {!hasAnyContact && channel.OutreachStage === "idle" && (
        <p className="text-xs text-muted-foreground">
          还没有联系方式 — 点"编辑"加 email / Telegram / Discord
        </p>
      )}

      {(hasAnyContact || channel.OutreachStage !== "idle") && (
        <dl className="grid grid-cols-1 gap-y-2 text-sm md:grid-cols-2">
          {channel.ContactEmail && (
            <Field label="Email">
              <a href={`mailto:${channel.ContactEmail}`} className="text-primary hover:underline">
                {channel.ContactEmail}
              </a>
            </Field>
          )}
          {channel.ContactTelegram && (
            <Field label="Telegram">
              <code className="rounded bg-muted px-1 text-xs">{channel.ContactTelegram}</code>
            </Field>
          )}
          {channel.ContactDiscord && (
            <Field label="Discord">
              <code className="rounded bg-muted px-1 text-xs">{channel.ContactDiscord}</code>
            </Field>
          )}
          {channel.ContactAgent && <Field label="经纪人 / 代理">{channel.ContactAgent}</Field>}
          {lastContactRelative && (
            <Field label="最近联络">
              <span className="text-xs">
                {fmtTimestamp(channel.LastContactedAt)}
                <span className="ml-1 text-muted-foreground">({lastContactRelative})</span>
              </span>
            </Field>
          )}
          {channel.NextFollowupAt && (
            <Field label="下次跟进">
              <span className={cn("text-xs", followupOverdue && "text-destructive font-medium")}>
                {fmtTimestamp(channel.NextFollowupAt)}
                {followupOverdue && <span className="ml-1">⚠ 已过期</span>}
              </span>
            </Field>
          )}
          {channel.ContactNotes && (
            <div className="col-span-1 md:col-span-2">
              <dt className="text-xs uppercase tracking-wide text-muted-foreground">备注</dt>
              <dd className="mt-1 whitespace-pre-wrap rounded bg-muted/30 p-2 text-xs">
                {channel.ContactNotes}
              </dd>
            </div>
          )}
        </dl>
      )}

      {editing && (
        <EditContactModal
          channel={channel}
          onClose={() => setEditing(false)}
          onSaved={() => setEditing(false)}
        />
      )}
    </>
  );
}

function EditContactModal({
  channel,
  onClose,
  onSaved,
}: {
  channel: Channel;
  onClose: () => void;
  onSaved: () => void;
}) {
  const qc = useQueryClient();
  // Date input wants YYYY-MM-DD; convert from RFC3339 if present.
  const initFollowup = channel.NextFollowupAt
    ? channel.NextFollowupAt.slice(0, 10)
    : "";
  const [form, setForm] = useState({
    contact_email:    channel.ContactEmail ?? "",
    contact_telegram: channel.ContactTelegram ?? "",
    contact_discord:  channel.ContactDiscord ?? "",
    contact_agent:    channel.ContactAgent ?? "",
    contact_notes:    channel.ContactNotes ?? "",
    outreach_stage:   (channel.OutreachStage || "idle") as OutreachStage,
    next_followup_date: initFollowup,
  });
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (body: UpdateChannelContactRequest) =>
      endpoints.updateChannelContact(channel.ID, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["channel", channel.ID] });
      qc.invalidateQueries({ queryKey: ["channels"] });
      onSaved();
    },
    onError: (err) => setError((err as Error).message),
  });

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const body: UpdateChannelContactRequest = {
      contact_email:    form.contact_email,
      contact_telegram: form.contact_telegram,
      contact_discord:  form.contact_discord,
      contact_agent:    form.contact_agent,
      contact_notes:    form.contact_notes,
      outreach_stage:   form.outreach_stage,
      next_followup_at: form.next_followup_date
        ? new Date(`${form.next_followup_date}T00:00:00Z`).toISOString()
        : "",
    };
    mutation.mutate(body);
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <form
        className="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-lg border border-border bg-background p-5 shadow-lg"
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
      >
        <h2 className="mb-1 text-lg font-semibold">
          编辑 BD 联络信息
        </h2>
        <p className="mb-4 text-xs text-muted-foreground">
          保存时 <strong>最近联络时间</strong> 会自动更新为现在 — 把这个动作当作"我刚跟这个 KOL 通过气"的信号。
        </p>
        <div className="grid grid-cols-2 gap-3">
          {/* v2.4.3 — field order reflects edit frequency in audit_log:
              `channel.contact_update` fires mostly for "I just talked to
              this KOL, add a log line + bump next-followup". Notes +
              followup go up top; static contact info (email/TG/Discord)
              moves below the fold. */}
          <label className="col-span-2 block">
            <span className="mb-1 block text-xs text-muted-foreground">备注（沟通日志）</span>
            <textarea
              className="h-24 w-full resize-y rounded-md border border-border bg-background px-3 py-2 text-sm"
              value={form.contact_notes}
              onChange={(e) => setForm({ ...form, contact_notes: e.target.value })}
              placeholder={'eg. "5/13 发邮件介绍合作 / 5/15 回价 $8k / 我们 reply $6k 等回复"'}
              autoFocus
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs text-muted-foreground">下次跟进日期（可选）</span>
            <Input
              type="date"
              value={form.next_followup_date}
              onChange={(e) => setForm({ ...form, next_followup_date: e.target.value })}
            />
            <p className="mt-1 text-[10px] text-muted-foreground">
              过了这个日期还没编辑，详情页会显示 ⚠ 已过期 提醒。
            </p>
          </label>
          <label className="block">
            <span className="mb-1 block text-xs text-muted-foreground">外联状态</span>
            <Select
              value={form.outreach_stage}
              onChange={(e) => setForm({ ...form, outreach_stage: e.target.value as OutreachStage })}
            >
              {(Object.entries(OUTREACH_STAGES) as [OutreachStage, { label: string }][]).map(([k, v]) => (
                <option key={k} value={k}>{k} ({v.label})</option>
              ))}
            </Select>
          </label>
          <div className="col-span-2 mt-2 border-t border-border/60 pt-2 text-xs text-muted-foreground">
            联络渠道（一次填好后基本不动）
          </div>
          <label className="block">
            <span className="mb-1 block text-xs text-muted-foreground">Email</span>
            <Input
              type="email"
              value={form.contact_email}
              onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
              placeholder="kol@example.com"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs text-muted-foreground">Telegram</span>
            <Input
              value={form.contact_telegram}
              onChange={(e) => setForm({ ...form, contact_telegram: e.target.value })}
              placeholder="@username"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs text-muted-foreground">Discord</span>
            <Input
              value={form.contact_discord}
              onChange={(e) => setForm({ ...form, contact_discord: e.target.value })}
              placeholder="username#1234"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs text-muted-foreground">经纪人 / 代理</span>
            <Input
              value={form.contact_agent}
              onChange={(e) => setForm({ ...form, contact_agent: e.target.value })}
              placeholder="姓名 + 邮箱 / 公司名"
            />
          </label>
        </div>
        {error && <p className="mt-3 text-sm text-destructive">{error}</p>}
        <div className="mt-4 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            取消
          </Button>
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? "保存中…" : "保存"}
          </Button>
        </div>
      </form>
    </div>
  );
}
